import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { FieldArray, Formik } from 'formik'
import * as Yup from 'yup'
import { flatten, compact } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useDragDropMonitor } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { Copy, DotsSixVertical, PencilSimple } from '@phosphor-icons/react'

import { toastFactory } from '@/factories'

import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { formValidation } from '../../FormLegacy/validation'
import { generateId } from '../../../utils'
import Avatar from '../../../Avatar'
import CollectionItem from '../../Collection/CollectionItem'
import Accordion from '../../Accordion/Accordion'
import BuildingAnalyticIcons from '../../BuildingAnalytics/BuildingAnalyticIcons/BuildingAnalyticIcons'
import OrderArrows from '../../OrderArrows/OrderArrows'
import { sortByOrder } from '../../../reducers/Organisation/bookTypesReducer'

import { computeCascadeOrder } from './OrganisationBooking'
import ServiceQuestionItem from './ServiceQuestionItem'

const validationSchema = Yup.object().shape({
  questions: Yup.array().of(
    Yup.object().shape({
      title: formValidation.required,
      answers: Yup.array().of(
        Yup.object().shape({
          title: formValidation.required,
        }),
      ),
    }),
  ),
})

const ServiceBookingTypeItem = ({
  index,
  bookingType,
  questions,
  saveQuestion,
  updateQuestion,
  reorderQuestion,
  deleteQuestion,
  saveAnswer,
  updateAnswer,
  deleteAnswer,
  onClick,
  isExpanded,
  editBookingType,
  reorder,
  reorderProgressing,
  resetDragTree,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { displaySnackbar } = useSnackbar()

  const [formError, setFormError] = React.useState('')
  const [updatePending, setUpdatePending] = React.useState(false)
  const { name, internalName, icon, color, id } = bookingType
  const { ref: dragRef, handleRef } = useSortable({
    id: `bookingType-${id}`,
    index,
    disabled: isExpanded,
    type: 'bookingType',
    group: String(bookingType.category),
    // See the matching comment on the category-level useSortable call in
    // OrganisationBooking.jsx: these lists are strictly independent, nothing crosses layers.
    accept: 'bookingType',
  })

  const onFormSubmit = ({ questions: updatedQuestions }, { setSubmitting }) => {
    setFormError('')
    const isTypeError = updatedQuestions
      .filter(({ selectType }) => selectType === 'multiple')
      .map(({ answers }) => answers.find(({ pricingType }) => pricingType === 'custom price'))

    if (compact(isTypeError)[0]) {
      setSubmitting(false)
      return setFormError(t('organisation:booking.type.errors.customPriceOnMultiple'))
    }

    const deletedQuestions = questions.filter(
      ({ id }) => !updatedQuestions.some(({ id: qId }) => qId === id),
    )
    const deletedAnswers = flatten(
      updatedQuestions.map(({ id, answers, answersType }) => {
        const currentQuestion = questions.find(({ id: qId }) => id === qId)
        const questionExistingAnswers = currentQuestion ? currentQuestion.answers : []
        const answersForDelete =
          answersType && answersType !== 'button'
            ? questionExistingAnswers.filter(({ type }) => type === 'button')
            : questionExistingAnswers.filter(
                ({ id: aId }) => !answers.some((answer) => answer.id === aId),
              )
        return answersForDelete.map((a) => ({ answerId: a.id, questionId: id }))
      }),
    )

    setUpdatePending(true)
    Promise.all([
      ...deletedQuestions.map((question) => deleteQuestion(question.id)),
      ...deletedAnswers.map(({ questionId, answerId }) => deleteAnswer(questionId, answerId)),
      ...updatedQuestions.map((question) => {
        if (question.answersType && question.answersType !== 'button') {
          question.answers = [
            {
              title: t('organisation:booking.type.answer.defaultTitle'),
              type: question.answersType,
              pricingType: 'add',
              pricingValue: '',
              placeholder: t('organisation:booking.type.answer.defaultPlaceholder'),
            },
          ]
        }
        if ('id' in question) {
          const {
            answers: _answers,
            bookingTypeId: _bookingTypeId,
            clientId: _clientId,
            ...questionData
          } = question
          return updateQuestion({ ...questionData }, question.id)
        } else {
          const { clientId: _clientId, ...questionData } = question
          return saveQuestion({
            ...questionData,
            answers: question.answers.map((answer, i) => ({
              ...answer,
              type: question.answersType,
              order: i,
            })),
          })
        }
      }),
      ...updatedQuestions.map((question) => {
        return question.answers.map((answer, i) => {
          if ('id' in answer) {
            return updateAnswer(
              { ...answer, type: question.answersType, order: i },
              answer.id,
              question.id,
            )
          } else if ('id' in question) {
            return saveAnswer({ ...answer, order: i }, question.id)
          }
          return null
        })
      }),
    ])
      .then(() => {
        // todo handle responses
        displaySnackbar(t('organisation:booking.type.toasts.saved'))
      })
      .catch(() => {
        displaySnackbar(t('organisation:booking.type.errors.saveFailed'), true)
        // todo handle error
      })
      .finally(() => {
        setSubmitting(false)
        setUpdatePending(false)
      })
  }

  // Kept in sync during each render of the FieldArray below (a plain ref write, not read back
  // in the same render) so the drag-end handler below always sees the latest Formik state
  // without needing to be recreated — and without depending on the values/arrayHelpers deps
  // changing on every keystroke, which would otherwise churn the useDragDropMonitor subscription.
  const questionsFormRef = useRef(null)

  // Listens on the single, page-level DragDropProvider (see OrganisationBooking.jsx) instead of
  // owning a separate nested one: a DragDropProvider nested inside another one doesn't reliably
  // report the actual drop position (https://github.com/clauderic/dnd-kit/issues/1633, #1564,
  // #1664) — the library's own multi-list examples use one shared provider with per-list `group`
  // scoping instead, which is what `group: String(id)` below opts into.
  const handleQuestionDragEnd = useCallback(
    (event) => {
      const { canceled, operation } = event
      if (canceled) return
      const { source } = operation
      if (!source || !isSortable(source) || source.type !== 'question') return
      if (source.initialGroup !== String(id)) return
      if (source.group !== source.initialGroup) {
        resetDragTree()
        return
      }

      const formState = questionsFormRef.current
      if (!formState) return
      const { values, arrayHelpers, setFieldValue } = formState

      const sourceIndex = source.initialIndex
      const targetIndex = source.index
      if (sourceIndex === targetIndex) return

      const reordered = [...values.questions]
      const [moved] = reordered.splice(sourceIndex, 1)
      reordered.splice(targetIndex, 0, moved)

      const updates = computeCascadeOrder(reordered, targetIndex)

      arrayHelpers.move(sourceIndex, targetIndex)

      // updates[0] is the moved item at targetIndex; any further entries are items cascaded
      // forward from targetIndex + 1, so their position in the (now-moved) array lines up
      // directly with their position in `updates`.
      updates.forEach(({ id: qId, order }, i) => {
        setFieldValue(`questions.${targetIndex + i}.order`, order)
        if (typeof qId === 'number') {
          reorderQuestion(qId, order)?.catch?.((err) => console.warn(err))
        }
      })
    },
    [id, reorderQuestion, resetDragTree],
  )

  useDragDropMonitor({ onDragEnd: handleQuestionDragEnd })

  return (
    <Accordion
      rootRef={dragRef}
      collapsed={!isExpanded}
      additionalClass={`ctp-shop-item${isExpanded ? ' ctp-shop-item--open' : ''}`}
    >
      <Accordion.Head handleClick={onClick}>
        <CollectionItem border={false} padding={false} hover={false}>
          <span
            ref={handleRef}
            className={`ctp-shop-item__drag-handle${isExpanded ? ' ctp-shop-item__drag-handle--disabled' : ''}`}
          >
            <DotsSixVertical size={20} />
          </span>
          {color && (
            <Avatar
              background={color}
              style={{ color: '#ffffff', width: '30px', height: '30px', flexShrink: 0 }}
            >
              {icon && <BuildingAnalyticIcons name={icon} />}
            </Avatar>
          )}
          <div className="ctp-shop-item__name-wrap">
            <span className="ctp-shop-item__name ctp-shop-item__name--wrap">{name}</span>
            {internalName ? (
              <span className="ctp-shop-item__internal-name ctp-shop-item__name--wrap">
                {internalName}
              </span>
            ) : null}
          </div>
          <OrderArrows
            disabled={reorderProgressing}
            upClick={() => {
              reorder({ category: bookingType.category, index, direction: -1 })
            }}
            downClick={() => {
              reorder({ category: bookingType.category, index, direction: 1 })
            }}
          />
          <button
            className="button ctp-shop-item__icon-button"
            aria-label={t('organisation:services.item.duplicate')}
            onClick={() => editBookingType(id, true)}
          >
            <Copy size={16} />
          </button>
          <button
            className="button ctp-shop-item__icon-button"
            aria-label={t('common:actions.edit')}
            onClick={() => editBookingType(id)}
          >
            <PencilSimple size={16} />
          </button>
        </CollectionItem>
      </Accordion.Head>
      <Accordion.Body>
        <Formik
          enableReinitialize={!updatePending}
          initialValues={{ questions: sortByOrder(questions) || [] }}
          validationSchema={validationSchema}
          onSubmit={onFormSubmit}
        >
          {({ values, handleSubmit, isSubmitting, isValid, dirty, setFieldValue }) => {
            return (
              <React.Fragment>
                <FieldArray name={'questions'}>
                  {(arrayHelpers) => {
                    questionsFormRef.current = { values, arrayHelpers, setFieldValue }
                    return (
                      <div className="ctp-shop-item__sku-list">
                        {values.questions.map((question, index) => {
                          return (
                            <ServiceQuestionItem
                              key={`questionItem-${question.id ?? question.clientId}`}
                              index={index}
                              question={question}
                              bookingType={bookingType}
                              questionArrayHelpers={arrayHelpers}
                              deleteServiceQuestion={() => {
                                // Deletion has to hit the API directly instead of just removing
                                // from Formik's array: `enableReinitialize` snaps the form back
                                // to the Redux-sourced `questions` prop on the next unrelated
                                // re-render, silently undoing a purely local removal. Persisting
                                // via `deleteQuestion` updates that Redux state, so reinit no
                                // longer resurrects the deleted question.
                                if (!('id' in question)) {
                                  arrayHelpers.remove(index)
                                  return
                                }
                                return deleteQuestion(question.id)
                                  .then(() => {
                                    arrayHelpers.remove(index)
                                    toastFactory.success(
                                      t('organisation:booking.type.toasts.questionDeleted'),
                                    )
                                  })
                                  .catch(() => {
                                    toastFactory.error(
                                      t('organisation:booking.type.errors.saveFailed'),
                                    )
                                  })
                              }}
                              deleteAnswer={deleteAnswer}
                              reorderQuestion={reorderQuestion}
                            />
                          )
                        })}
                        <button
                          className="button"
                          onClick={() => {
                            arrayHelpers.push({
                              title: '',
                              isRequired: false,
                              selectType: 'single',
                              modal: '',
                              bookingTypeId: bookingType.id,
                              answers: [],
                              order: values.questions.length + 1,
                              clientId: generateId(),
                            })
                          }}
                        >
                          {t('organisation:booking.question.addQuestion')}
                        </button>
                        <button
                          className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                          onClick={handleSubmit}
                          disabled={isSubmitting || !isValid || !dirty}
                        >
                          {t('common:actions.save')}
                        </button>
                      </div>
                    )
                  }}
                </FieldArray>
                {formError && (
                  <p className={'help is-danger'} style={{ textAlign: 'center' }}>
                    {formError}
                  </p>
                )}
              </React.Fragment>
            )
          }}
        </Formik>
      </Accordion.Body>
    </Accordion>
  )
}

ServiceBookingTypeItem.propTypes = {
  index: PropTypes.number,
  bookingType: PropTypes.object,
  questions: PropTypes.array,
  saveQuestion: PropTypes.func,
  updateQuestion: PropTypes.func,
  reorderQuestion: PropTypes.func,
  deleteQuestion: PropTypes.func,
  saveAnswer: PropTypes.func,
  updateAnswer: PropTypes.func,
  deleteAnswer: PropTypes.func,
  onClick: PropTypes.func,
  isExpanded: PropTypes.bool,
  editBookingType: PropTypes.func,
  reorder: PropTypes.func,
  reorderProgressing: PropTypes.bool,
  resetDragTree: PropTypes.func,
}

export default ServiceBookingTypeItem
